import React, { memo, useMemo } from 'react';
import { CheckCircle, Circle } from 'lucide-react';

interface ProgressBarProps {
  currentSection: number;
  totalSections: number;
  completedSections: Set<string>;
  sectionTitles: string[];
  progress?: number; // Optional overall progress percentage
  onSectionClick?: (sectionIndex: number) => void; // Callback when section is clicked
}

const ProgressBar: React.FC<ProgressBarProps> = memo(({
  currentSection,
  totalSections,
  completedSections,
  sectionTitles,
  progress,
  onSectionClick
}) => {
  // Memoize progress calculation
  const progressPercentage = useMemo(() => {
    return progress !== undefined ? progress : (currentSection / totalSections) * 100;
  }, [progress, currentSection, totalSections]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      {/* Progress Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Progress</h2>
        <span className="text-sm text-gray-600">
          {currentSection} of {totalSections} sections
        </span>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <div className="text-sm text-gray-600 mt-2">
          {Math.round(progressPercentage)}% complete
        </div>
      </div>

      {/* Section List */}
      <div className="space-y-3">
        {sectionTitles.map((title, index) => {
          const sectionNumber = index + 1; // 1-based for display
          // IMPORTANT: completedSections uses 0-based index.toString() as keys
          const isCompleted = completedSections.has(index.toString());
          const isCurrent = sectionNumber === currentSection;
          const isUpcoming = sectionNumber > currentSection;

          // Allow clicking on completed sections or current section
          const isClickable = isCompleted || isCurrent || (sectionNumber <= currentSection);
          
          return (
            <div
              key={sectionNumber}
              onClick={() => {
                if (isClickable && onSectionClick) {
                  onSectionClick(index); // Pass 0-based index
                }
              }}
              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                isCurrent
                  ? 'bg-primary-50 border border-primary-200'
                  : isCompleted
                  ? 'bg-green-50 border border-green-200'
                  : isUpcoming
                  ? 'bg-gray-50 border border-gray-200'
                  : 'bg-white border border-gray-200'
              } ${
                isClickable && onSectionClick
                  ? 'cursor-pointer hover:shadow-md hover:scale-[1.02]'
                  : 'cursor-default'
              }`}
              title={
                isClickable && onSectionClick
                  ? `Click to jump to ${title}`
                  : undefined
              }
            >
              <div className="flex-shrink-0">
                {isCompleted ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <Circle
                    className={`w-5 h-5 ${
                      isCurrent
                        ? 'text-primary-500'
                        : isUpcoming
                        ? 'text-gray-400'
                        : 'text-gray-300'
                    }`}
                  />
                )}
              </div>
              <div className="flex-1">
                <div
                  className={`text-sm font-medium ${
                    isCurrent
                      ? 'text-primary-900'
                      : isCompleted
                      ? 'text-green-900'
                      : isUpcoming
                      ? 'text-gray-500'
                      : 'text-gray-700'
                  }`}
                >
                  {title}
                </div>
                <div
                  className={`text-xs ${
                    isCurrent
                      ? 'text-primary-600'
                      : isCompleted
                      ? 'text-green-600'
                      : isUpcoming
                      ? 'text-gray-400'
                      : 'text-gray-500'
                  }`}
                >
                  {isCurrent
                    ? 'In Progress'
                    : isCompleted
                    ? 'Completed'
                    : isUpcoming
                    ? 'Upcoming'
                    : 'Not Started'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
